import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function getSessionId() {
  let sid = localStorage.getItem("pc_session_id");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("pc_session_id", sid);
  }
  return sid;
}

export const api = axios.create({ baseURL: API, withCredentials: true });

// ---------- Auth ----------
export async function register(email, password, name, acceptedTerms) {
  const { data } = await api.post("/auth/register", {
    email, password, name, session_id: getSessionId(), accepted_terms: !!acceptedTerms,
  });
  return data;
}

export async function login(email, password) {
  const { data } = await api.post("/auth/login", {
    email, password, session_id: getSessionId(),
  });
  return data;
}

export async function googleSession(sessionId) {
  const { data } = await api.post("/auth/google/session", {
    session_id: sessionId, local_session_id: getSessionId(),
  });
  return data;
}

export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export async function logout() {
  const { data } = await api.post("/auth/logout");
  return data;
}

export async function forgotPassword(email) {
  const { data } = await api.post("/auth/forgot-password", {
    email, base_url: window.location.origin,
  });
  return data;
}

export async function resetPassword(token, password) {
  const { data } = await api.post("/auth/reset-password", { token, password });
  return data;
}

export async function billingStatus() {
  const { data } = await api.get("/billing/status");
  return data;
}

export async function startCheckout() {
  const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
  return data;
}

export async function openBillingPortal() {
  const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
  return data;
}

// ---------- Build Intelligence ----------
export async function getIntelligenceProfile() {
  const { data } = await api.get("/intelligence/profile");
  return data;
}
export async function authorizePlatform(platform) {
  const { data } = await api.post("/intelligence/authorize", { platform });
  return data;
}
export async function pauseLearning(value) {
  const { data } = await api.post("/intelligence/pause", { value });
  return data;
}
export async function setPersonalization(value) {
  const { data } = await api.post("/intelligence/personalization", { value });
  return data;
}
export async function disconnectPlatform(platform) {
  const { data } = await api.post("/intelligence/disconnect", { platform });
  return data;
}
export async function deleteIntelligenceData() {
  const { data } = await api.delete("/intelligence/data");
  return data;
}
export async function deleteIntelligenceProfile() {
  const { data } = await api.delete("/intelligence/profile");
  return data;
}

// ---------- Admin (owner only; server-enforced) ----------
export async function getAdminStats() {
  const { data } = await api.get("/admin/stats");
  return data;
}
export async function setAdminSettings(payload) {
  const { data } = await api.post("/admin/settings", payload);
  return data;
}
export async function getConfigCheck() {
  const { data } = await api.get("/admin/config-check");
  return data;
}
export async function getConnectors() {
  const { data } = await api.get("/connectors", { params: { session_id: getSessionId() } });
  return data;
}

export async function getGithubStatus() {
  const { data } = await api.get("/github/status");
  return data;
}
export async function getGithubInstallUrl() {
  const { data } = await api.get("/github/install-url");
  return data;
}
export async function getGithubReconnectUrl() {
  const { data } = await api.get("/github/reconnect-url");
  return data;
}
export async function getGithubManageUrl() {
  const { data } = await api.get("/github/manage-url");
  return data;
}
export async function disconnectGithub() {
  const { data } = await api.post("/github/disconnect");
  return data;
}

export async function auditPrompt(prompt, platform, signal) {
  const { data } = await api.post("/audit", {
    prompt,
    platform,
    session_id: getSessionId(),
  }, { signal });
  return data;
}

export async function getAudit(id) {
  const { data } = await api.get(`/audit/${id}`, {
    params: { session_id: getSessionId() },
  });
  return data;
}

export async function setAuditShare(id, isPublic) {
  const { data } = await api.post(`/audit/${id}/share`, {
    public: isPublic,
    session_id: getSessionId(),
  });
  return data;
}

export async function getHistory() {
  const { data } = await api.get("/history", {
    params: { session_id: getSessionId() },
  });
  return data;
}

export async function getAuditCount() {
  const { data } = await api.get("/audit-count", {
    params: { session_id: getSessionId() },
  });
  return data;
}

export async function captureEmail(email) {
  const { data } = await api.post("/email-capture", {
    session_id: getSessionId(),
    email,
  });
  return data;
}

export async function getPlatforms() {
  const { data } = await api.get("/platforms");
  return data.platforms;
}

export async function getShowcase() {
  const { data } = await api.get("/showcase", { params: { limit: 8 } });
  return data;
}

export async function getUsageModel() {
  const { data } = await api.get("/usage/model");
  return data;
}

export async function setUsageOverride(platform, value) {
  const { data } = await api.put("/usage/override", { platform, value });
  return data;
}

export async function getEmergentStatus() {
  const { data } = await api.get("/emergent/status", {
    params: { session_id: getSessionId() },
  });
  return data;
}

export async function startEmergentOAuth(origin) {
  const { data } = await api.get("/emergent/oauth/start", {
    params: { session_id: getSessionId(), origin },
  });
  return data;
}

export async function disconnectEmergent() {
  const { data } = await api.post("/emergent/disconnect", {
    session_id: getSessionId(),
  });
  return data;
}

export async function getBuildHistory() {
  const { data } = await api.get("/emergent/build-history", {
    params: { session_id: getSessionId() },
  });
  return data;
}

export async function getInsights() {
  const { data } = await api.get("/insights", {
    params: { session_id: getSessionId() },
  });
  return data;
}
